import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * Time chart (docs/08 "Timeline: Segmente + Marker + Heute-Linie"): one labelled track per row, segments and markers
 * positioned by fractions 0..1 of the visible range, optional today line. Used for occupancy per year (design/Hausakte),
 * slice tracks of a unit (design/Einheit) and billing periods (design/Zeitraeume). Segments grow in (iaGrow).
 */
export type SegmentTone = 'quiet' | 'tenancy' | 'ended' | 'tenancy-new' | 'shared' | 'vacancy' | 'planned' | 'soft' | 'strong' | 'warm' | 'ok' | 'outline' | 'expected';
/** `labelOutside`: the label stands right of the segment (for segments too short for their text, e.g. a short period). */
export type TimeSegment = { key: string; start: number; end: number; tone: SegmentTone; label?: ReactNode; title?: string; to?: string; labelOutside?: boolean };
/** `to`: the marker opens that page. */
export type TimeMarker = { key: string; at: number; title: string; to?: string };
export type TimeRow = { key: string; label: ReactNode; sub?: ReactNode; to?: string; segments: TimeSegment[]; markers?: TimeMarker[] };

const clamp = (n: number) => Math.max(0, Math.min(1, n));
const pct = (n: number) => `${(clamp(n) * 100).toFixed(3)}%`;

/**
 * Wider than its box (phones, many years): the row labels stay pinned on the left, the chart opens scrolled to today
 * and a bar that starts left of the visible part keeps its name readable at the visible start (full text in title
 * and aria-label).
 */
export function TimeChart({ ticks, rows, today, todayLabel, label, size = 'md', track = 'striped', labelWidth = 150, minWidth = 760, span, open = 'today', maxHeight }: {
  ticks: { key: string; at: number; label: ReactNode }[];
  rows: TimeRow[];
  today?: number | null;
  todayLabel?: ReactNode;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  track?: 'striped' | 'lines' | 'plain';
  labelWidth?: number;
  minWidth?: number;
  /**
   * The track is `span` times as wide as the visible part (e.g. 26 years with 4 visible → 6.5); the rest is reached by
   * scrolling sideways. Without it the chart fills its box and only `minWidth` makes it scroll.
   */
  span?: number;
  /** Where a scrolling chart opens: today about three quarters across, or the right end. */
  open?: 'today' | 'end';
  /** Caps the height: rows scroll inside and the year scale stays on top (sticky). */
  maxHeight?: number | string;
}) {
  const wide = span && span > 1 ? `max(${minWidth}px, calc(var(--tc-label) + 12px + (100% - var(--tc-label) - 12px) * ${span.toFixed(4)}))` : minWidth;
  const style = { '--tc-label': `${labelWidth}px`, minWidth: wide } as CSSProperties;
  const lines = ticks.filter((t) => t.at > 0.0001 && t.at < 0.9999);
  const showToday = today != null && today >= 0 && today <= 1;
  const scroller = useRef<HTMLDivElement>(null);
  const opened = useRef(false);
  const pinRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    let frame = 0;
    // names of bars cut off on the left move to the visible start of the bar
    const pin = () => {
      frame = 0;
      const label = el.querySelector<HTMLElement>('.tc-label');
      const visibleLeft = (label ? label.getBoundingClientRect().right : el.getBoundingClientRect().left) + 4;
      for (const seg of el.querySelectorAll<HTMLElement>('.tc-seg')) {
        const r = seg.getBoundingClientRect();
        const shift = r.left < visibleLeft && r.right > visibleLeft ? Math.min(visibleLeft - r.left, Math.max(0, r.width - 40)) : 0;
        seg.style.setProperty('--tc-pin', `${Math.round(shift)}px`);
      }
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(pin); };
    pinRef.current = schedule;
    // once more after the grow-in animation of the bars
    const late = window.setTimeout(schedule, 900);
    el.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.clearTimeout(late);
      el.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  useEffect(() => {
    const el = scroller.current;
    if (el && !opened.current && (showToday || open === 'end') && rows.length) {
      opened.current = true;
      const track = el.querySelector<HTMLElement>('.tc-track');
      if (open === 'end') el.scrollLeft = el.scrollWidth;
      else if (track && el.scrollWidth > el.clientWidth + 1) {
        // open at today: today about three quarters across the part of the track that is not under the pinned labels
        const box = el.getBoundingClientRect();
        const tr = track.getBoundingClientRect();
        const cover = tr.left - box.left + el.scrollLeft;
        const todayX = cover + today! * tr.width;
        el.scrollLeft = Math.max(0, todayX - cover - 0.75 * (el.clientWidth - cover));
      }
    }
    pinRef.current();
  });
  return (
    <div className={`tc-scroll${maxHeight != null ? ' is-capped' : ''}`} ref={scroller} style={maxHeight != null ? { maxHeight } : undefined}>
      <div className={`timechart ${size} track-${track}`} style={style} role="group" aria-label={label}>
        <div className="tc-row tc-scale" aria-hidden>
          <span />
          <span className="tc-ticks">
            {ticks.map((t) => <span key={t.key} className="tc-tick" style={{ left: pct(t.at) }}>{t.label}</span>)}
          </span>
        </div>
        {rows.map((r, ri) => (
          <div className="tc-row" key={r.key}>
            {r.to ? (
              <Link to={r.to} className="tc-label"><span className="tc-name">{r.label}</span>{r.sub && <span className="tc-sub">{r.sub}</span>}</Link>
            ) : (
              <span className="tc-label"><span className="tc-name">{r.label}</span>{r.sub && <span className="tc-sub">{r.sub}</span>}</span>
            )}
            <div className={`tc-track${!r.segments.length && r.markers?.length ? ' only-markers' : ''}`}>
              {lines.map((t) => <span key={t.key} className="tc-line" style={{ left: pct(t.at) }} aria-hidden />)}
              {r.segments.map((s, si) => {
                const st: CSSProperties = { left: `calc(${pct(s.start)} + 2px)`, width: `calc(${pct(s.end - s.start + 0)} - 4px)`, animationDelay: `${Math.round(clamp(s.start) * 400 + ri * 30 + si * 20)}ms` };
                const cls = `tc-seg t-${s.tone}`;
                const inner = s.label != null && !s.labelOutside ? <span className="tc-seg-text">{s.label}</span> : null;
                const named = s.labelOutside || s.to ? { 'aria-label': s.title } : {};
                const seg = s.to ? (
                  <Link key={s.key} to={s.to} className={cls} style={st} title={s.title} {...named}>{inner}</Link>
                ) : (
                  <span key={s.key} className={cls} style={st} title={s.title}>{inner}</span>
                );
                if (!s.labelOutside || s.label == null) return seg;
                return [seg, <span key={`${s.key}-out`} className="tc-seg-out" style={{ left: `calc(${pct(s.end)} + 6px)` }} aria-hidden={s.to ? true : undefined}>{s.label}</span>];
              })}
              {r.markers?.map((m) => (m.to
                ? <Link key={m.key} to={m.to} className="tc-marker" style={{ left: pct(m.at) }} title={m.title} aria-label={m.title} />
                : <span key={m.key} className="tc-marker" style={{ left: pct(m.at) }} title={m.title} role="img" aria-label={m.title} />))}
              {showToday && <span className="tc-today" style={{ left: pct(today!) }} aria-hidden />}
            </div>
          </div>
        ))}
        {showToday && todayLabel && (
          <div className="tc-row tc-today-row">
            <span />
            <span className="tc-ticks"><span className="tc-today-label" style={{ left: pct(today!) }}>{todayLabel}</span></span>
          </div>
        )}
      </div>
    </div>
  );
}

export type CoverageStatus = 'covered' | 'manual' | 'review' | 'missing' | 'expected' | 'na';
/** 12 month coverage squares (docs/08 CoverageGrid). `label` describes all months for screen readers. */
export function CoverageGrid({ months, label, titles }: { months: CoverageStatus[]; label: string; titles?: string[] }) {
  return (
    <span className="coverage is-lg" role="img" aria-label={label}>
      {months.map((m, i) => <span key={i} className={`cov-${m}`} title={titles?.[i]} />)}
    </span>
  );
}
